import { useCallback, useEffect, useState } from 'react'
import type { JSX } from 'react'

import { formatDisplayDate, formatDisplayTime } from '@lensmira/i18n'

import { Ltr, useTranslation } from '../i18n.js'
import { DataSurface } from './DataSurface.js'
import { statusLine } from '../queue/status-line.js'
import { useWriteSettled } from '../queue/use-settled-write.js'
import { useWriteQueue } from '../queue/use-write-queue.js'
import { sentence } from '../sentence.js'
import {
  messageChannelWord,
  messageKindLabel,
  messageMoveLabel,
  messageStatusWord,
  suppressionReasonWord,
} from '../status-labels.js'

/**
 * What the shop has said to customers, and what did not get there. §6.17 `MSG-005`, `OPS-142`.
 *
 * **This is the screen `MSG-005` is about.** "Delivery status tracking and failure surfacing. A
 * silent WhatsApp failure means an uncollected job." The failure is the point: a queued message
 * nobody looks at is a customer standing at the counter asking why nobody called.
 *
 * **Nothing in v1 sends.** `MSG-001` gives email and in-app, `MSG-001a` records that a Pakistani
 * shop's customers use neither, and the stated mitigation is "printed and downloadable artifacts
 * the shop sends themselves". So the row carries the text to read out or copy, and "I sent this
 * myself" is a real action here rather than a transport's callback. The consent check runs again at
 * that moment (§12 case 57), which is why a hand-sent message can come back suppressed instead.
 *
 * **A failure needs a reason and the server refuses one without.** `MSG-005` is about surfacing the
 * failure, and a failure with no reason surfaces nothing, so the field is required at the point of
 * saying it failed rather than optional and usually empty.
 *
 * **`carriedRxData` is on the row.** §8.1 makes a prescription special-category data, and a message
 * that went out carrying one is a different thing to have sent, whatever the template says today.
 *
 * **The customer is a id here and that is a gap, not a choice.** `MessageView` carries
 * `customerId` and no name: Appendix N encrypts a name at rest and the server does not decrypt one
 * into this list. The rendered body does hold it, because `customer_name` is filled at render, so
 * the text is what a person reads the row by, and opening the customer is one click away.
 */

export interface MessageData {
  readonly id: string
  readonly kind: string
  readonly channel: string
  /** Appendix C's message lifecycle. Unnarrowed per `ARC-020`. */
  readonly status: string
  readonly customerId: string
  readonly locale: string
  readonly scheduledFor: string
  readonly version: number
  readonly attemptCount: number
  readonly carriedRxData: boolean
  readonly subject?: string
  readonly body?: string
  readonly failureReason?: string
  readonly suppressionReason?: string
}

export interface OutboxProps {
  readonly load: (status?: string) => Promise<readonly MessageData[]>
  /** The branch's own zone, so a send time reads as the shop's clock and not as UTC. */
  readonly timezone: string
  readonly onOpenCustomer?: (customerId: string) => void
}

/** `MESSAGE_TRANSITIONS`, as the moves a person makes. The server holds the real table. */
const MOVES: Readonly<Record<string, readonly string[]>> = {
  queued: ['sending', 'cancelled'],
  // `bounced` on both, as `MESSAGE_TRANSITIONS` has it. Leaving it off meant a shopkeeper who
  // learned the address was dead could only record `failed`, which offers `sending` again: the
  // retry the domain says must not exist, because "a bounce is the address being wrong, so
  // retrying it is sending to the same wrong address". The status filter offered the state too, so
  // the screen named one it could not produce.
  sending: ['sent', 'failed', 'bounced'],
  sent: ['delivered', 'failed', 'bounced'],
  failed: ['sending', 'cancelled'],
}

/** The two that `MSG-005` makes name a reason. */
const NEEDS_REASON: readonly string[] = ['failed', 'bounced']

export function Outbox({ load, timezone, onOpenCustomer }: OutboxProps): JSX.Element {
  const { t, locale } = useTranslation()
  const { enqueue, snapshot } = useWriteQueue()
  const [status, setStatus] = useState('')
  const [rows, setRows] = useState<readonly MessageData[] | null>(null)
  const [reasons, setReasons] = useState<Record<string, string>>({})
  /** The form's own complaint (no failure reason). Never a reason to replace the list. */
  const [error, setError] = useState<string | null>(null)
  /** A failed read, kept apart from `error` so a missing reason cannot blank the outbox. */
  const [loadError, setLoadError] = useState<string | null>(null)
  const [writeId, setWriteId] = useState<string | null>(null)

  const refresh = useCallback(async () => {
    try {
      setRows(await load(status === '' ? undefined : status))
      setLoadError(null)
    } catch (caught) {
      setLoadError(caught instanceof Error ? caught.message : t('outbox.loadFailed'))
    }
  }, [load, status, t])

  useEffect(() => {
    void refresh()
  }, [refresh])

  useWriteSettled(snapshot, writeId, () => {
    setWriteId(null)
    setReasons({})
    void refresh()
  })

  const move = useCallback(
    async (row: MessageData, to: string) => {
      const reason = reasons[row.id]?.trim() ?? ''
      if (NEEDS_REASON.includes(to) && reason === '') {
        // The server refuses a blank with the same reasoning, so saying it here saves a queue entry
        // and says it in the place the person is looking.
        setError(t('outbox.reasonRequired'))
        return
      }
      setError(null)
      const write = (await enqueue({
        method: 'POST',
        path: `/messages/${row.id}/status`,
        label: t('outbox.queueLabel'),
        body: {
          status: to,
          expectedVersion: row.version,
          ...(NEEDS_REASON.includes(to) ? { failureReason: reason } : {}),
        },
      })) as { id: string }
      setWriteId(write.id)
    },
    [enqueue, reasons, t],
  )

  const when = (iso: string): string => {
    const instant = Date.parse(iso)
    if (!Number.isFinite(instant)) return iso
    return `${formatDisplayDate(instant, timezone, locale)} ${formatDisplayTime(instant, timezone, locale)}`
  }

  const line = statusLine(t, snapshot, writeId, t('queue.subject.message'))

  return (
    <section className="lm-screen" aria-labelledby="outbox-heading">
      <h1 id="outbox-heading" className="lm-screen__heading">
        {t('outbox.heading')}
      </h1>

      <label className="lm-field lm-field--inline">
        <span className="lm-field__label">{t('outbox.statusLabel')}</span>
        <select
          className="lm-input"
          value={status}
          onChange={(e) => {
            setStatus(e.currentTarget.value)
          }}
        >
          {/* Absent is the server's own open set, which is queued, sending and failed: the three
              that still need something to happen to them. */}
          <option value="">{t('outbox.statusOpen')}</option>
          <option value="queued">{t('messageStatus.queued')}</option>
          <option value="sending">{t('messageStatus.sending')}</option>
          <option value="sent">{t('messageStatus.sent')}</option>
          <option value="delivered">{t('messageStatus.delivered')}</option>
          <option value="failed">{t('messageStatus.failed')}</option>
          <option value="bounced">{t('messageStatus.bounced')}</option>
          <option value="suppressed">{t('messageStatus.suppressed')}</option>
          <option value="cancelled">{t('messageStatus.cancelled')}</option>
        </select>
      </label>

      {error === null ? null : (
        <p className="lm-hint" role="alert">
          {error}
        </p>
      )}

      {line === null ? null : (
        <p className="lm-queue-state" role="status">
          {line}
        </p>
      )}

      {/*
        Blank status is the server's open set (queued, sending, failed), so nothing there is
        first-run. A chosen status that matches nothing is a filter that matched nothing.
      */}
      <DataSurface
        state={
          loadError !== null
            ? {
                kind: 'error',
                message: loadError,
                onRetry: () => {
                  setLoadError(null)
                  setRows(null)
                  void refresh()
                },
              }
            : rows === null
              ? { kind: 'loading' }
              : rows.length === 0
                ? { kind: status === '' ? 'empty' : 'filtered' }
                : { kind: 'ready' }
        }
        shape="rows"
        rows={4}
        what={t('outbox.what')}
        emptyMessage={t('outbox.empty')}
        filteredMessage={t('outbox.filtered')}
      >
        <ul className="lm-message-list">
          {(rows ?? []).map((row) => (
            <li key={row.id} className="lm-message">
              <span className="lm-message__kind">{messageKindLabel(t, row.kind)}</span>
              <span className="lm-message__channel">{messageChannelWord(t, row.channel)}</span>
              <span className="lm-message__status">{messageStatusWord(t, row.status)}</span>

              <span className="lm-message__when">
                {/* `OPS-140` decided this moment in the branch's own time, so it is read back in
                    the branch's own time: a UTC stamp on a Pakistani till is five hours wrong. */}
                {sentence(t, 'outbox.scheduled', { when: <Ltr>{when(row.scheduledFor)}</Ltr> })}
              </span>

              {row.attemptCount > 0 ? (
                <span className="lm-message__attempts">
                  {t('outbox.attempts', { count: row.attemptCount })}
                </span>
              ) : null}

              {row.carriedRxData ? (
                // §8.1: this one carried prescription values, which is a different thing to have
                // sent and stays true of the row whatever the template says now.
                <span className="lm-message__rx">{t('outbox.carriedRx')}</span>
              ) : null}

              {row.subject === undefined ? null : (
                <span className="lm-message__subject">{row.subject}</span>
              )}

              {row.body === undefined ? null : (
                // The text itself, because `MSG-001a`'s mitigation is the shop sending it by hand:
                // somebody is going to read this down a phone or paste it into something.
                <p className="lm-message__body">{row.body}</p>
              )}

              {row.failureReason === undefined ? null : (
                <span className="lm-message__failure">
                  {t('outbox.failedBecause', { reason: row.failureReason })}
                </span>
              )}

              {row.suppressionReason === undefined ? null : (
                // `MSG-004`: not sent, and here is why. A record rather than a deletion, and the
                // only answer to the question anybody asks.
                <span className="lm-message__suppressed">
                  {t('outbox.notSentBecause', {
                    reason: suppressionReasonWord(t, row.suppressionReason),
                  })}
                </span>
              )}

              {onOpenCustomer === undefined ? null : (
                <button
                  type="button"
                  className="lm-button lm-button--secondary"
                  onClick={() => {
                    onOpenCustomer(row.customerId)
                  }}
                >
                  {t('outbox.openCustomer')}
                </button>
              )}

              {(MOVES[row.status] ?? []).some((to) => NEEDS_REASON.includes(to)) ? (
                <label className="lm-field lm-field--inline">
                  <span className="lm-field__label">{t('outbox.reasonLabel')}</span>
                  <input
                    className="lm-input"
                    autoComplete="off"
                    value={reasons[row.id] ?? ''}
                    onChange={(e) => {
                      const value = e.currentTarget.value
                      setReasons((previous) => ({ ...previous, [row.id]: value }))
                    }}
                  />
                </label>
              ) : null}

              {(MOVES[row.status] ?? []).map((to) => (
                <button
                  key={to}
                  type="button"
                  className={
                    to === 'cancelled' || to === 'failed'
                      ? 'lm-button lm-button--secondary'
                      : 'lm-button lm-button--primary'
                  }
                  onClick={() => void move(row, to)}
                >
                  {messageMoveLabel(t, to)}
                </button>
              ))}
            </li>
          ))}
        </ul>
      </DataSurface>
    </section>
  )
}
